import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';

export type OralExamTransitionVariant = 'openBriefing' | 'startExam';

export type OralExamTransitionProgressDialogProps = {
  isOpen: boolean;
  variant: OralExamTransitionVariant;
  isPending: boolean;
  error?: string | null;
  onRetry: () => void;
  onClose: () => void;
};

/**
 * Lecturer briefing/start progress on the MeetingConfirm overlay host.
 *
 * LiveKit video hides Gluestack AlertDialog / app-root `useDialog()`. The
 * MeetingConfirm scrim is viewport-fixed so it greys out the whole screen,
 * not the ~100px start-gate row. Wrap-up cancel still uses `useDialog()`.
 */
export function OralExamTransitionProgressDialog({
  isOpen,
  variant,
  isPending,
  error,
  onRetry,
  onClose,
}: OralExamTransitionProgressDialogProps) {
  const { t } = useTranslation();
  const { showProgress, hideProgress } = useMeetingConfirm();
  const shownRef = useRef(false);

  const titleKey = variant === 'openBriefing'
    ? 'oralExam.lecturer.transitionProgress.openBriefing.title'
    : 'oralExam.lecturer.transitionProgress.startExam.title';
  const line1Key = variant === 'openBriefing'
    ? 'oralExam.lecturer.transitionProgress.openBriefing.line1'
    : 'oralExam.lecturer.transitionProgress.startExam.line1';
  const line2Key = variant === 'openBriefing'
    ? 'oralExam.lecturer.transitionProgress.openBriefing.line2'
    : 'oralExam.lecturer.transitionProgress.startExam.line2';
  const line3Key = variant === 'openBriefing'
    ? 'oralExam.lecturer.transitionProgress.openBriefing.line3'
    : 'oralExam.lecturer.transitionProgress.startExam.line3';

  const title = t(titleKey, {
    defaultValue: variant === 'openBriefing'
      ? 'Opening group briefing'
      : 'Starting the exam',
  });
  const line1 = t(line1Key, {
    defaultValue: variant === 'openBriefing'
      ? 'Opening the shared room…'
      : 'Preparing to start…',
  });
  const line2 = t(line2Key, {
    defaultValue: variant === 'openBriefing'
      ? 'Notifying students…'
      : 'Students stay in the briefing until the rooms are ready.',
  });
  const line3 = t(line3Key, {
    defaultValue: variant === 'openBriefing'
      ? 'Preparing exam rooms in the background. This can take several seconds.'
      : 'This usually takes a few seconds.',
  });

  useEffect(() => {
    if (!isOpen) {
      if (shownRef.current) {
        hideProgress();
        shownRef.current = false;
      }
      return undefined;
    }

    shownRef.current = true;
    showProgress({
      title,
      lines: [line1, line2, line3],
      pending: isPending,
      error,
      onRetry,
      onClose,
    });
    return undefined;
  }, [
    error,
    hideProgress,
    isOpen,
    isPending,
    line1,
    line2,
    line3,
    onClose,
    onRetry,
    showProgress,
    title,
  ]);

  useEffect(() => () => {
    if (shownRef.current) {
      hideProgress();
      shownRef.current = false;
    }
  }, [hideProgress]);

  return null;
}
